// Setup › Webhooks › Incoming (WEBHOOKS-IN; design 2026-10-27 §2, §7; managers). An outside
// system posts to a hook's URL, signed with its secret; each accepted delivery becomes one task
// "<hook name> · <received time>" for the hook's digital employee (optionally marked for one of
// its agents), with the payload attached. The list, the hook dialog (name, signing, DE, agent,
// board, rate; "More": allowed IPs, size cap, retention, screen), pause / resume, the
// secret-once dialog (create, rotate), Send sample and the delivery log with each body and a
// replay. Explanations sit behind (i) tips, never under the fields.
import type { OperationInput, OperationOutput } from "@companyos/sdk";
import {
  Badge,
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Field,
  FieldLabel,
  InfoTip,
  Input,
  NativeSelect,
  NativeSelectOption,
  Notice,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
  toast,
  workerName,
} from "@companyos/ui";
import {
  infiniteQueryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  ChevronDownIcon,
  CopyIcon,
  FlaskConicalIcon,
  HistoryIcon,
  KeyRoundIcon,
  PauseIcon,
  PencilIcon,
  PlayIcon,
  PlusIcon,
  Trash2Icon,
  WebhookIcon,
} from "lucide-react";
import { type FormEvent, type ReactNode, useEffect, useId, useState } from "react";
import type { Api } from "../../api/client";
import type { CompanyAccess } from "../company";
import { formatDateTime } from "../format";
import { digitalEmployeesQuery, workforceAgentsQuery } from "../queries";
import { LoadError, Loading, LoadMore } from "../screen-parts";
import { problemText } from "../settings/problem-text";
import { DENSE_TABLE, RowAction, RowActions } from "../settings/row-actions";
import { boardsQuery } from "../work/work-queries";
import { type Secret, SecretDialog } from "./webhooks-screen";

type Hook = OperationOutput<"inboundWebhooks.list">["data"][number];
type Delivery = OperationOutput<"inboundWebhooks.deliveries">["data"][number];
type CreateBody = OperationInput<"inboundWebhooks.create">["body"];
type Verification = NonNullable<CreateBody["verification"]>;
type ScreenMode = NonNullable<CreateBody["screen_mode"]>;

const hooksKey = (co: string) => ["company", co, "inbound-webhooks"] as const;
const deliveriesKey = (co: string, id: string) =>
  ["company", co, "inbound-webhooks", id, "deliveries"] as const;

const inboundQuery = (api: Api, co: string) =>
  infiniteQueryOptions({
    queryKey: hooksKey(co),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      api.client.inboundWebhooks.list({
        params: { co },
        query: { limit: 50, ...(pageParam ? { cursor: pageParam } : {}) },
      }),
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });

const deliveriesQuery = (api: Api, co: string, id: string) =>
  infiniteQueryOptions({
    queryKey: deliveriesKey(co, id),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      api.client.inboundWebhooks.deliveries({
        params: { co, id },
        query: { limit: 25, ...(pageParam ? { cursor: pageParam } : {}) },
      }),
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });

const VERIFICATION: { value: Verification; label: string }[] = [
  { value: "companyos", label: "CompanyOS" },
  { value: "stripe", label: "Stripe" },
  { value: "github", label: "GitHub" },
];
const SECRET_NOTE = "Shown once. The sender signs every delivery with it.";

/** Where senders post: the API's public origin (same origin when the app has no API URL). */
export function endpointUrl(api: Api, path: string): string {
  const origin = typeof window === "undefined" ? "http://localhost" : window.location.origin;
  const root = api.baseUrl ? new URL(api.baseUrl, origin).href.replace(/\/$/, "") : origin;
  return `${root}${path}`;
}

async function copyText(text: string, what: string) {
  try {
    await navigator.clipboard.writeText(text);
    toast.add({ title: `${what} copied`, type: "success" });
  } catch {
    toast.add({
      title: `Copy failed: select the ${what.toLowerCase()} and copy it`,
      type: "error",
    });
  }
}

export function IncomingWebhooks({ api, company }: { api: Api; company: CompanyAccess }) {
  const co = company.id;
  const q = useInfiniteQuery(inboundQuery(api, co));
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Hook | "new" | null>(null);
  const [secret, setSecret] = useState<Secret | null>(null);
  const [deliveriesOf, setDeliveriesOf] = useState<Hook | null>(null);
  const [rotating, setRotating] = useState<Hook | null>(null);
  const [deleting, setDeleting] = useState<Hook | null>(null);
  const rows = q.data?.pages.flatMap((p) => p.data) ?? [];
  const refresh = () => void qc.invalidateQueries({ queryKey: hooksKey(co) });

  const setStatus = useMutation({
    mutationFn: (v: { hook: Hook; status: "active" | "paused" }) =>
      api.client.inboundWebhooks.update({
        params: { co, id: v.hook.id },
        body: { status: v.status },
      }),
    onSuccess: (h) => {
      refresh();
      toast.add({ title: h.status === "paused" ? "Paused" : "Resumed", type: "success" });
    },
    onError: (e) => toast.add({ title: problemText(e, "Not changed."), type: "error" }),
  });
  const sample = useMutation({
    mutationFn: (h: Hook) =>
      api.client.inboundWebhooks.sample({ params: { co, id: h.id }, body: {} }),
    onSuccess: (d) => {
      refresh();
      void qc.invalidateQueries({ queryKey: deliveriesKey(co, d.hook_id) });
      toast.add({ title: "Sample sent", type: "success" });
    },
    onError: (e) => toast.add({ title: problemText(e, "The sample was not sent."), type: "error" }),
  });

  return (
    <div data-slot="incoming-webhooks" className="flex min-w-0 flex-col gap-3">
      <div className="flex justify-end">
        <Button size="sm" onClick={() => setEditing("new")}>
          <PlusIcon data-icon="inline-start" />
          New incoming webhook
        </Button>
      </div>
      {q.isPending ? (
        <Loading label="Loading incoming webhooks" rows={3} />
      ) : q.isError ? (
        <LoadError what="Incoming webhooks" error={q.error} />
      ) : !rows.length ? (
        <EmptyState
          variant="card"
          icon={WebhookIcon}
          title="No incoming webhooks yet"
          actions={
            <Button size="sm" variant="secondary" onClick={() => setEditing("new")}>
              New
            </Button>
          }
        />
      ) : (
        <>
          <Table data-slot="incoming-webhooks-table" className={DENSE_TABLE}>
            <TableCaption className="sr-only">Incoming webhooks of the company.</TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead scope="col">Incoming webhook</TableHead>
                <TableHead scope="col" className="max-md:hidden">
                  Last delivery
                </TableHead>
                <TableHead scope="col" className="max-md:hidden">
                  24 h
                </TableHead>
                <TableHead scope="col">
                  <span className="sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((h) => (
                <TableRow key={h.id} data-inbound-webhook={h.id} className="group/row">
                  <TableHead scope="row" className="min-w-0 font-medium">
                    <span className="flex flex-wrap items-baseline gap-x-2">
                      <span className="truncate">{h.name}</span>
                      {h.status !== "active" ? (
                        <span
                          data-slot="inbound-status"
                          className="text-xs font-normal text-muted-foreground"
                        >
                          {h.status === "paused" ? "Paused" : "Disabled"}
                        </span>
                      ) : null}
                    </span>
                    <span
                      data-slot="inbound-target"
                      className="block text-xs font-normal text-muted-foreground"
                    >
                      → task for {workerName(h.target_name)}
                      {h.for_agent_name ? ` · for ${workerName(h.for_agent_name)}` : ""}
                      {h.board_name ? ` · on ${h.board_name}` : ""}
                    </span>
                  </TableHead>
                  <TableCell className="text-muted-foreground max-md:hidden">
                    {h.last_received_at ? formatDateTime(h.last_received_at) : "—"}
                  </TableCell>
                  <TableCell className="tabular-nums max-md:hidden" data-slot="inbound-24h">
                    {h.received_24h}
                  </TableCell>
                  <TableCell className="w-px">
                    <RowActions>
                      <RowAction
                        icon={PencilIcon}
                        label={`Edit ${h.name}`}
                        tip="Edit"
                        onClick={() => setEditing(h)}
                      />
                      <RowAction
                        icon={CopyIcon}
                        label={`Copy URL of ${h.name}`}
                        tip="Copy URL"
                        onClick={() => void copyText(endpointUrl(api, h.endpoint_path), "URL")}
                      />
                      <RowAction
                        icon={FlaskConicalIcon}
                        label={`Send sample to ${h.name}`}
                        tip="Send sample"
                        disabled={sample.isPending || h.status !== "active"}
                        onClick={() => sample.mutate(h)}
                      />
                      <RowAction
                        icon={HistoryIcon}
                        label={`Deliveries of ${h.name}`}
                        tip="Deliveries"
                        onClick={() => setDeliveriesOf(h)}
                      />
                      <RowAction
                        icon={h.status === "active" ? PauseIcon : PlayIcon}
                        label={`${h.status === "active" ? "Pause" : "Resume"} ${h.name}`}
                        tip={h.status === "active" ? "Pause" : "Resume"}
                        disabled={setStatus.isPending}
                        onClick={() =>
                          setStatus.mutate({
                            hook: h,
                            status: h.status === "active" ? "paused" : "active",
                          })
                        }
                      />
                      <RowAction
                        icon={KeyRoundIcon}
                        label={`Rotate secret of ${h.name}`}
                        tip="Rotate secret"
                        onClick={() => setRotating(h)}
                      />
                      <RowAction
                        icon={Trash2Icon}
                        label={`Delete ${h.name}`}
                        tip="Delete"
                        className="hover:text-danger-text"
                        onClick={() => setDeleting(h)}
                      />
                    </RowActions>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <LoadMore
            what="incoming webhooks"
            hasMore={q.hasNextPage}
            loading={q.isFetchingNextPage}
            onMore={() => void q.fetchNextPage()}
          />
        </>
      )}
      {editing ? (
        <HookDialog
          key={editing === "new" ? "new" : editing.id}
          api={api}
          company={company}
          hook={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSecret={setSecret}
        />
      ) : null}
      {secret ? <SecretDialog secret={secret} onClose={() => setSecret(null)} /> : null}
      {rotating ? (
        <RotateDialog
          api={api}
          company={company}
          hook={rotating}
          onClose={() => setRotating(null)}
          onSecret={setSecret}
        />
      ) : null}
      {deleting ? (
        <DeleteDialog
          api={api}
          company={company}
          hook={deleting}
          onClose={() => setDeleting(null)}
          onDone={refresh}
        />
      ) : null}
      {deliveriesOf ? (
        <DeliveriesDialog
          api={api}
          company={company}
          hook={deliveriesOf}
          onClose={() => setDeliveriesOf(null)}
        />
      ) : null}
    </div>
  );
}

/** A label with its (i) tip. */
function TipLabel({
  htmlFor,
  tip,
  children,
}: {
  htmlFor: string;
  tip?: string;
  children: ReactNode;
}) {
  return (
    <span className="flex items-center gap-1">
      <FieldLabel htmlFor={htmlFor}>{children}</FieldLabel>
      {tip ? <InfoTip label={`About ${String(children).toLowerCase()}`}>{tip}</InfoTip> : null}
    </span>
  );
}

const intOr = (s: string, fallback: number) => {
  const n = Number.parseInt(s, 10);
  return Number.isFinite(n) ? n : fallback;
};

function HookDialog({
  api,
  company,
  hook,
  onClose,
  onSecret,
}: {
  api: Api;
  company: CompanyAccess;
  hook: Hook | null;
  onClose: () => void;
  onSecret: (s: Secret) => void;
}) {
  const co = company.id;
  const qc = useQueryClient();
  const ids = {
    name: useId(),
    verification: useId(),
    secret: useId(),
    de: useId(),
    agent: useId(),
    board: useId(),
    rate: useId(),
    cidrs: useId(),
    size: useId(),
    retention: useId(),
    screen: useId(),
    url: useId(),
  };
  const des = useInfiniteQuery(digitalEmployeesQuery(api, co));
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = des;
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) void fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);
  const agents = useQuery(workforceAgentsQuery(api, co));
  const boards = useQuery(boardsQuery(api, co));
  const deOptions = (des.data?.pages.flatMap((p) => p.data) ?? []).filter(
    (d) => d.status !== "retired" || d.membership_id === hook?.target_membership_id,
  );

  const [name, setName] = useState(hook?.name ?? "");
  const [verification, setVerification] = useState<Verification>(hook?.verification ?? "companyos");
  const [stripeSecret, setStripeSecret] = useState("");
  const [target, setTarget] = useState(hook?.target_membership_id ?? "");
  const [agent, setAgent] = useState(hook?.for_agent_id ?? "");
  const [board, setBoard] = useState(hook?.board_id ?? "");
  const [rate, setRate] = useState(String(hook?.rate_per_minute ?? 60));
  const [cidrs, setCidrs] = useState((hook?.allowed_cidrs ?? []).join("\n"));
  const initialKb = String(Math.round((hook?.max_bytes ?? 262_144) / 1024));
  const [sizeKb, setSizeKb] = useState(initialKb);
  const [retention, setRetention] = useState(String(hook?.payload_retention_days ?? 30));
  const [screen, setScreen] = useState<ScreenMode>(hook?.screen_mode ?? "company");
  const [more, setMore] = useState(false);
  const [touched, setTouched] = useState(false);
  const agentOptions = (agents.data ?? []).filter(
    (a) => a.reports_to_membership_id === target && (a.status !== "retired" || a.id === agent),
  );

  const save = useMutation({
    mutationFn: async () => {
      const settings = {
        name: name.trim(),
        target_membership_id: target,
        rate_per_minute: intOr(rate, 60),
        allowed_cidrs: cidrs
          .split(/[\s,]+/)
          .map((c) => c.trim())
          .filter(Boolean),
        // An edit keeps the exact byte value unless the size field was changed.
        ...(hook && sizeKb === initialKb ? {} : { max_bytes: intOr(sizeKb, 256) * 1024 }),
        payload_retention_days: intOr(retention, 30),
        screen_mode: screen,
      };
      if (hook) {
        await api.client.inboundWebhooks.update({
          params: { co, id: hook.id },
          body: { ...settings, for_agent_id: agent || null, board_id: board || null },
        });
        return null;
      }
      const created = await api.client.inboundWebhooks.create({
        params: { co },
        body: {
          ...settings,
          verification,
          ...(agent ? { for_agent_id: agent } : {}),
          ...(board ? { board_id: board } : {}),
          ...(verification === "stripe" ? { secret: stripeSecret.trim() } : {}),
        },
      });
      return created;
    },
    onSuccess: (created) => {
      void qc.invalidateQueries({ queryKey: hooksKey(co) });
      onClose();
      if (created?.secret)
        onSecret({
          url: created.inbound_webhook.name,
          secret: created.secret,
          created: true,
          note: SECRET_NOTE,
        });
      else
        toast.add({
          title: hook ? "Incoming webhook saved" : "Incoming webhook created",
          type: "success",
        });
    },
  });
  const nameBad = !name.trim();
  const targetBad = !target;
  const secretBad = !hook && verification === "stripe" && stripeSecret.trim().length < 16;
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!nameBad && !targetBad && !secretBad) save.mutate();
  };

  return (
    <Dialog open onOpenChange={(o) => !o && !save.isPending && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>{hook ? "Edit incoming webhook" : "New incoming webhook"}</DialogTitle>
          </DialogHeader>
          <Field data-invalid={(touched && nameBad) || undefined}>
            <FieldLabel htmlFor={ids.name}>Name</FieldLabel>
            <Input
              id={ids.name}
              value={name}
              placeholder="Stripe payments"
              aria-invalid={(touched && nameBad) || undefined}
              onChange={(e) => setName(e.target.value)}
            />
            {touched && nameBad ? (
              <p role="alert" className="text-xs text-danger-text">
                Name it.
              </p>
            ) : null}
          </Field>
          {hook ? (
            <Field>
              <FieldLabel htmlFor={ids.url}>URL</FieldLabel>
              <div className="flex min-w-0 gap-2">
                <Input
                  id={ids.url}
                  readOnly
                  value={endpointUrl(api, hook.endpoint_path)}
                  className="min-w-0 font-mono text-xs"
                  onFocus={(e) => e.currentTarget.select()}
                />
                <Button
                  type="button"
                  variant="outline"
                  aria-label="Copy URL"
                  onClick={() => void copyText(endpointUrl(api, hook.endpoint_path), "URL")}
                >
                  <CopyIcon aria-hidden="true" />
                </Button>
              </div>
            </Field>
          ) : null}
          <Field>
            <TipLabel
              htmlFor={ids.verification}
              tip="How a delivery proves it came from the sender. CompanyOS: X-CompanyOS-Signature. Stripe: Stripe-Signature, with Stripe's signing secret. GitHub: X-Hub-Signature-256."
            >
              Signing
            </TipLabel>
            <NativeSelect
              id={ids.verification}
              className="w-full"
              value={verification}
              disabled={Boolean(hook)}
              onChange={(e) => setVerification(e.target.value as Verification)}
            >
              {VERIFICATION.map((v) => (
                <NativeSelectOption key={v.value} value={v.value}>
                  {v.label}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          </Field>
          {!hook && verification === "stripe" ? (
            <Field data-invalid={(touched && secretBad) || undefined}>
              <TipLabel
                htmlFor={ids.secret}
                tip="Stripe › Developers › Webhooks › your endpoint › Signing secret (whsec_…)."
              >
                Stripe signing secret
              </TipLabel>
              <Input
                id={ids.secret}
                className="font-mono text-xs"
                autoComplete="off"
                value={stripeSecret}
                aria-invalid={(touched && secretBad) || undefined}
                onChange={(e) => setStripeSecret(e.target.value)}
              />
              {touched && secretBad ? (
                <p role="alert" className="text-xs text-danger-text">
                  Paste Stripe's signing secret.
                </p>
              ) : null}
            </Field>
          ) : null}
          <Field data-invalid={(touched && targetBad) || undefined}>
            <TipLabel
              htmlFor={ids.de}
              tip="Each delivery becomes a task for this digital employee's seat, which wakes it. The payload is attached."
            >
              Digital employee
            </TipLabel>
            <NativeSelect
              id={ids.de}
              className="w-full"
              value={target}
              aria-invalid={(touched && targetBad) || undefined}
              onChange={(e) => {
                setTarget(e.target.value);
                setAgent("");
              }}
            >
              <NativeSelectOption value="">Pick one</NativeSelectOption>
              {deOptions.map((d) => (
                <NativeSelectOption key={d.membership_id} value={d.membership_id}>
                  {workerName(d.display_name)}
                </NativeSelectOption>
              ))}
            </NativeSelect>
            {touched && targetBad ? (
              <p role="alert" className="text-xs text-danger-text">
                Pick a digital employee.
              </p>
            ) : null}
          </Field>
          <Field>
            <TipLabel
              htmlFor={ids.agent}
              tip="An agent that reports to this digital employee. The task goes to the DE, marked for the agent."
            >
              For agent
            </TipLabel>
            <NativeSelect
              id={ids.agent}
              className="w-full"
              value={agent}
              disabled={!target}
              onChange={(e) => setAgent(e.target.value)}
            >
              <NativeSelectOption value="">None</NativeSelectOption>
              {agentOptions.map((a) => (
                <NativeSelectOption key={a.id} value={a.id}>
                  {workerName(a.name)} (via its DE)
                </NativeSelectOption>
              ))}
            </NativeSelect>
          </Field>
          <Field>
            <TipLabel htmlFor={ids.board} tip="Default: the company's first team board.">
              Board
            </TipLabel>
            <NativeSelect
              id={ids.board}
              className="w-full"
              value={board}
              onChange={(e) => setBoard(e.target.value)}
            >
              <NativeSelectOption value="">Default</NativeSelectOption>
              {(boards.data?.data ?? []).map((b) => (
                <NativeSelectOption key={b.id} value={b.id}>
                  {b.name}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          </Field>
          <Field>
            <TipLabel htmlFor={ids.rate} tip="Deliveries over this per minute are refused (429).">
              Rate per minute
            </TipLabel>
            <Input
              id={ids.rate}
              type="number"
              min={1}
              max={600}
              inputMode="numeric"
              value={rate}
              onChange={(e) => setRate(e.target.value)}
            />
          </Field>
          <Collapsible open={more} onOpenChange={setMore}>
            <CollapsibleTrigger render={<Button type="button" variant="ghost" size="xs" />}>
              <ChevronDownIcon
                aria-hidden="true"
                className={more ? "rotate-180 transition-transform" : "transition-transform"}
              />
              More
            </CollapsibleTrigger>
            <CollapsibleContent className="flex flex-col gap-4 pt-3">
              <Field>
                <TipLabel htmlFor={ids.cidrs} tip="One IP or CIDR per line. Empty: any address.">
                  Allowed IPs
                </TipLabel>
                <Textarea
                  id={ids.cidrs}
                  rows={2}
                  className="font-mono text-xs"
                  placeholder="203.0.113.0/24"
                  value={cidrs}
                  onChange={(e) => setCidrs(e.target.value)}
                />
              </Field>
              <Field>
                <TipLabel htmlFor={ids.size} tip="1 to 1024 KB. Larger bodies are refused (413).">
                  Size cap (KB)
                </TipLabel>
                <Input
                  id={ids.size}
                  type="number"
                  min={1}
                  max={1024}
                  inputMode="numeric"
                  value={sizeKb}
                  onChange={(e) => setSizeKb(e.target.value)}
                />
              </Field>
              <Field>
                <TipLabel
                  htmlFor={ids.retention}
                  tip="Delivery log bodies are cleared after this many days (1–90). Task files stay with their task."
                >
                  Log retention (days)
                </TipLabel>
                <Input
                  id={ids.retention}
                  type="number"
                  min={1}
                  max={90}
                  inputMode="numeric"
                  value={retention}
                  onChange={(e) => setRetention(e.target.value)}
                />
              </Field>
              <Field>
                <TipLabel
                  htmlFor={ids.screen}
                  tip="Company: the company's content screen setting. Enforce: flagged data is withheld from the task."
                >
                  Screen
                </TipLabel>
                <NativeSelect
                  id={ids.screen}
                  className="w-full"
                  value={screen}
                  onChange={(e) => setScreen(e.target.value as ScreenMode)}
                >
                  <NativeSelectOption value="company">Company</NativeSelectOption>
                  <NativeSelectOption value="enforce">Enforce</NativeSelectOption>
                </NativeSelect>
              </Field>
            </CollapsibleContent>
          </Collapsible>
          {save.isError ? (
            <Notice tone="danger" role="alert">
              {problemText(save.error, "Not saved.")}
            </Notice>
          ) : null}
          <DialogFooter>
            <DialogClose render={<Button variant="secondary" />} disabled={save.isPending}>
              Cancel
            </DialogClose>
            <Button type="submit" disabled={save.isPending}>
              {save.isPending ? "Saving…" : hook ? "Save" : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function RotateDialog({
  api,
  company,
  hook,
  onClose,
  onSecret,
}: {
  api: Api;
  company: CompanyAccess;
  hook: Hook;
  onClose: () => void;
  onSecret: (s: Secret) => void;
}) {
  const stripe = hook.verification === "stripe";
  const [given, setGiven] = useState("");
  const secretId = useId();
  const rotate = useMutation({
    mutationFn: () =>
      api.client.inboundWebhooks.rotateSecret({
        params: { co: company.id, id: hook.id },
        body: stripe ? { secret: given.trim() } : {},
      }),
    onSuccess: (r) => {
      onClose();
      if (r.secret)
        onSecret({ url: hook.name, secret: r.secret, created: false, note: SECRET_NOTE });
      else toast.add({ title: "Secret replaced", type: "success" });
    },
  });
  return (
    <Dialog open onOpenChange={(o) => !o && !rotate.isPending && onClose()}>
      <DialogContent>
        <DialogHeader>
          <span className="flex items-center gap-1">
            <DialogTitle>Rotate the secret?</DialogTitle>
            <InfoTip label="About rotating">The old secret still verifies for 24 hours.</InfoTip>
          </span>
        </DialogHeader>
        {stripe ? (
          <Field>
            <FieldLabel htmlFor={secretId}>New Stripe signing secret</FieldLabel>
            <Input
              id={secretId}
              className="font-mono text-xs"
              autoComplete="off"
              value={given}
              onChange={(e) => setGiven(e.target.value)}
            />
          </Field>
        ) : null}
        {rotate.isError ? (
          <Notice tone="danger" role="alert">
            {problemText(rotate.error, "The secret was not rotated.")}
          </Notice>
        ) : null}
        <DialogFooter>
          <DialogClose render={<Button variant="secondary" />} disabled={rotate.isPending}>
            Cancel
          </DialogClose>
          <Button
            disabled={rotate.isPending || (stripe && given.trim().length < 16)}
            onClick={() => rotate.mutate()}
          >
            {rotate.isPending ? "Rotating…" : "Rotate secret"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeleteDialog({
  api,
  company,
  hook,
  onClose,
  onDone,
}: {
  api: Api;
  company: CompanyAccess;
  hook: Hook;
  onClose: () => void;
  onDone: () => void;
}) {
  const del = useMutation({
    mutationFn: () =>
      api.client.inboundWebhooks.delete({ params: { co: company.id, id: hook.id } }),
    onSuccess: () => {
      onDone();
      toast.add({ title: "Incoming webhook deleted", type: "success" });
      onClose();
    },
  });
  return (
    <Dialog open onOpenChange={(o) => !o && !del.isPending && onClose()}>
      <DialogContent>
        <DialogHeader>
          <span className="flex items-center gap-1">
            <DialogTitle>Delete this incoming webhook?</DialogTitle>
            <InfoTip label="About deleting">
              {hook.name} stops taking deliveries and its delivery log is removed. Tasks it made
              stay.
            </InfoTip>
          </span>
        </DialogHeader>
        {del.isError ? (
          <Notice tone="danger" role="alert">
            {problemText(del.error, "Not deleted.")}
          </Notice>
        ) : null}
        <DialogFooter>
          <DialogClose render={<Button variant="secondary" />} disabled={del.isPending}>
            Cancel
          </DialogClose>
          <Button variant="destructive" disabled={del.isPending} onClick={() => del.mutate()}>
            {del.isPending ? "Deleting…" : "Delete"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const STATUS_BADGE: Record<Delivery["status"], "success" | "warning" | "danger" | "neutral"> = {
  accepted: "success",
  dropped: "neutral",
  rejected: "danger",
  rate_limited: "warning",
  paused: "warning",
};
const STATUS_TEXT: Record<Delivery["status"], string> = {
  accepted: "Accepted",
  dropped: "Dropped",
  rejected: "Rejected",
  rate_limited: "Rate limited",
  paused: "Paused",
};
/** A delivery that may still hold its body (the refusals never kept one). */
const mayHaveBody = (d: Delivery) =>
  (d.status === "accepted" || d.status === "dropped") && !d.body_purged_at;

function formatBytes(n: number): string {
  return n < 1024 ? `${n} B` : `${(n / 1024).toFixed(n < 10_240 ? 1 : 0)} KB`;
}

function DeliveriesDialog({
  api,
  company,
  hook,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  hook: Hook;
  onClose: () => void;
}) {
  const co = company.id;
  const qc = useQueryClient();
  const q = useInfiniteQuery(deliveriesQuery(api, co, hook.id));
  const rows = q.data?.pages.flatMap((p) => p.data) ?? [];
  const [open, setOpen] = useState<string | null>(null);
  const body = useQuery({
    queryKey: [...deliveriesKey(co, hook.id), open, "body"],
    enabled: open !== null,
    queryFn: () =>
      api.client.inboundWebhooks.getDelivery({
        params: { co, id: hook.id, delivery_id: open ?? "" },
      }),
  });
  const replay = useMutation({
    mutationFn: (d: Delivery) =>
      api.client.inboundWebhooks.replayDelivery({
        params: { co, id: hook.id, delivery_id: d.id },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: deliveriesKey(co, hook.id) });
      void qc.invalidateQueries({ queryKey: hooksKey(co) });
      toast.add({ title: "Replayed", type: "success" });
    },
    onError: (e) => toast.add({ title: problemText(e, "Not replayed."), type: "error" }),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>Deliveries</DialogTitle>
          <p className="text-sm text-muted-foreground">{hook.name}</p>
        </DialogHeader>
        {q.isPending ? (
          <Loading label="Loading deliveries" rows={3} />
        ) : q.isError ? (
          <LoadError what="Deliveries" error={q.error} />
        ) : !rows.length ? (
          <EmptyState variant="card" icon={WebhookIcon} title="No deliveries yet" />
        ) : (
          <div className="flex max-h-[60vh] min-w-0 flex-col gap-3 overflow-auto">
            <Table data-slot="inbound-deliveries" className={DENSE_TABLE}>
              <TableCaption className="sr-only">Deliveries, newest first.</TableCaption>
              <TableHeader>
                <TableRow>
                  <TableHead scope="col">Received</TableHead>
                  <TableHead scope="col">Status</TableHead>
                  <TableHead scope="col">Size</TableHead>
                  <TableHead scope="col">Task</TableHead>
                  <TableHead scope="col">
                    <span className="sr-only">Actions</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((d) => (
                  <TableRow
                    key={d.id}
                    data-delivery={d.id}
                    data-state={open === d.id ? "selected" : undefined}
                  >
                    <TableHead scope="row" className="font-normal">
                      <Button
                        variant="link"
                        size="xs"
                        className="h-auto px-0"
                        aria-label={`Body of the delivery at ${formatDateTime(d.received_at)}`}
                        aria-expanded={open === d.id}
                        onClick={() => setOpen(open === d.id ? null : d.id)}
                      >
                        {formatDateTime(d.received_at)}
                      </Button>
                      {d.sample ? (
                        <span className="ml-1 text-xs text-muted-foreground">sample</span>
                      ) : d.replayed_from ? (
                        <span className="ml-1 text-xs text-muted-foreground">replay</span>
                      ) : null}
                    </TableHead>
                    <TableCell>
                      <span className="flex flex-wrap items-center gap-1">
                        <Badge variant={STATUS_BADGE[d.status]}>{STATUS_TEXT[d.status]}</Badge>
                        {d.screen?.verdict === "flagged" ? (
                          <Badge variant="outline" className="text-muted-foreground">
                            flagged
                          </Badge>
                        ) : null}
                      </span>
                      {d.reject_reason ? (
                        <span className="block text-xs text-muted-foreground">
                          {d.reject_reason}
                        </span>
                      ) : null}
                    </TableCell>
                    <TableCell className="tabular-nums text-muted-foreground">
                      {formatBytes(d.bytes)}
                    </TableCell>
                    <TableCell>
                      {d.task_id ? (
                        <Link
                          to="/c/$slug/work"
                          params={{ slug: company.slug }}
                          search={{ task: d.task_id } as never}
                          className="text-xs underline-offset-2 hover:underline"
                        >
                          Open task
                        </Link>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {mayHaveBody(d) ? (
                        <Button
                          size="sm"
                          variant="secondary"
                          aria-label={`Replay the delivery at ${formatDateTime(d.received_at)}`}
                          disabled={replay.isPending}
                          onClick={() => replay.mutate(d)}
                        >
                          Replay
                        </Button>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <LoadMore
              what="deliveries"
              hasMore={q.hasNextPage}
              loading={q.isFetchingNextPage}
              onMore={() => void q.fetchNextPage()}
            />
            {open ? (
              <section aria-label="Delivery body" className="flex min-w-0 flex-col gap-1">
                {body.isPending ? (
                  <Loading label="Loading the body" rows={1} />
                ) : body.isError ? (
                  <LoadError what="The body" error={body.error} />
                ) : body.data.body === null ? (
                  <p className="text-sm text-muted-foreground">
                    {body.data.body_purged_at ? "Body purged." : "No body stored."}
                  </p>
                ) : (
                  <pre
                    data-slot="inbound-delivery-body"
                    className="max-h-64 overflow-auto rounded-md border border-border bg-muted p-3 font-mono text-xs"
                  >
                    {JSON.stringify(body.data.body, null, 2)}
                  </pre>
                )}
              </section>
            ) : null}
          </div>
        )}
        <DialogFooter>
          <DialogClose render={<Button variant="secondary" />}>Close</DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
